import { Badge } from '@synoptic/ui-kit/badge/badge.js';
import { Button } from '@synoptic/ui-kit/button/button.js';
import { ArrowUpRightIcon } from '@synoptic/ui-kit/icons/react/arrow-up-right.js';
import { DollarIcon } from '@synoptic/ui-kit/icons/react/dollar.js';
import { StarIcon } from '@synoptic/ui-kit/icons/react/star.js';
import { UserIcon } from '@synoptic/ui-kit/icons/react/user.js';
import clsx from 'clsx';
import { useNavigate } from 'react-router';
import {
  StreamCardFragmentFragment,
  StreamCategory,
  UserBadgeFragmentFragment,
} from '@/__generated__/graphql';
import { S3Avatar } from '@/components/s3/user/s3-avatar.tsx';
import { getStreamCategoryTitle } from '@/components/streams/stream-categories.ts';
import { userAvatar } from '@/components/user-badge/user-badge.css.ts';
import { paths } from '@/routes-utils/paths.ts';
import { convertCentsToDollars } from '@/utils/convert-cents-to-dollars.ts';
import { formatNumber } from '@/utils/format-number';
import { getMonthlyPrice } from '@/utils/paid-data.ts';
import {
  homeCardBadge,
  homeStreamCard,
  homeStreamCardAdditionalInfo,
  homeStreamCardAdditionalInfoRight,
  homeStreamCardAuthor,
  homeStreamCardAuthorUsername,
  homeStreamCardDescription,
  homeStreamCardFeaturedBadge,
  homeStreamCardFollowersBlock,
  homeStreamCardHeader,
  homeStreamCardPriceTag,
  homeStreamCardPriceValues,
  homeStreamCardTitle,
  homeStreamCardTitleVariants,
  homeStreamCardViewDetailsButton,
  streamCardFooter,
} from './home-streams-section.css.ts';

export const HomeStreamCardCategoryBadge = ({
  category,
}: {
  category: StreamCategory;
}) => {
  return (
    <Badge className={homeCardBadge}>
      <span>{getStreamCategoryTitle(category)}</span>
    </Badge>
  );
};

export const HomeStreamCardPriceTag = ({ price }: { price?: number }) => {
  return (
    <div className={homeStreamCardPriceTag}>
      <DollarIcon width={18} height={18} />
      <div className={homeStreamCardPriceValues}>
        <span>{price ? `${convertCentsToDollars(price)}/month` : 'Free'}</span>
      </div>
    </div>
  );
};

export const HomeStreamCardFollowersBlock = ({
  followersCount,
}: {
  followersCount: number;
}) => {
  return (
    <div className={homeStreamCardFollowersBlock}>
      <UserIcon width={16} height={16} />
      <span>{formatNumber(followersCount)}</span>
    </div>
  );
};

export const HomeStreamCardFeaturedBadge = ({
  short = false,
}: {
  short?: boolean;
}) => {
  return (
    <div className={homeStreamCardFeaturedBadge}>
      <StarIcon width={16} height={16} />
      {!short && <span>Featured</span>}
    </div>
  );
};

export const HomeCardNameAndDescription = ({
  name,
  description,
  size = 'medium',
  rowLimit,
}: {
  name: string;
  description?: string | null;
  size?: 'small' | 'medium';
  rowLimit?: {
    name?: number;
    description?: number;
  };
}) => {
  return (
    <div>
      <h3
        className={clsx(homeStreamCardTitle, homeStreamCardTitleVariants[size])}
        style={{
          WebkitLineClamp: rowLimit?.name || 2,
        }}
      >
        {name}
      </h3>
      {description && (
        <p
          className={homeStreamCardDescription}
          style={{
            WebkitLineClamp: rowLimit?.description || 3,
          }}
        >
          {description}
        </p>
      )}
    </div>
  );
};

export const HomeStreamAuthor = ({
  author,
  startWithBy = true,
}: {
  author: UserBadgeFragmentFragment;
  startWithBy?: boolean;
}) => {
  return (
    <div className={homeStreamCardAuthor}>
      <S3Avatar
        className={userAvatar}
        size={'small'}
        s3Key={author.avatar?.key}
        userId={author.id}
      />
      <span className={homeStreamCardAuthorUsername}>
        {startWithBy && 'by '}
        {author.username}
      </span>
    </div>
  );
};

export const HomeStreamCard = ({
  stream,
}: {
  stream: StreamCardFragmentFragment;
}) => {
  const navigate = useNavigate();
  return (
    <div className={homeStreamCard}>
      <header className={homeStreamCardHeader}>
        <HomeStreamCardCategoryBadge category={stream.streamCategory} />
        <HomeStreamCardPriceTag price={getMonthlyPrice(stream.paidData)} />
      </header>
      <main>
        <HomeCardNameAndDescription
          name={stream.name}
          description={stream.description}
        />
      </main>
      <footer className={streamCardFooter}>
        <div className={homeStreamCardAdditionalInfo}>
          <HomeStreamAuthor author={stream.author} />
          <div className={homeStreamCardAdditionalInfoRight}>
            <HomeStreamCardFollowersBlock
              followersCount={stream.followersCount}
            />
            {stream.isFeatured && <HomeStreamCardFeaturedBadge />}
          </div>
        </div>
        <Button
          variant="outline"
          size="small"
          fullWidth
          className={homeStreamCardViewDetailsButton}
          onClick={() => {
            navigate(paths.makeStream(stream.id));
          }}
        >
          View Stream <ArrowUpRightIcon />
        </Button>
      </footer>
    </div>
  );
};
